import { invoke } from "@tauri-apps/api/core";
import { isEqual } from "es-toolkit";
import { type Getter, atom } from "jotai";
import { atomFamily, atomWithRefresh } from "jotai/utils";

import { Logger } from "@moment/logging";

import { getCache } from "../_interop/cache";
import { env } from "../env/env";
import { fs, remove } from "../filesystem";
import { paths } from "../paths";
import { $commandAvailability, type ScriptOutput } from "../terminal";
import { type EnvironmentVars } from "./environments.types";
import { interpolateEnvVars } from "./interpolateEnvVars";

const logger = new Logger("environments");

export interface EnvironmentInfo {
	name: string;
	var_count: number;
}

export const $environmentsInfo = atomWithRefresh(async () => {
	if (!env.isDesktopApp) {
		return [];
	}

	try {
		const infos = await invoke<EnvironmentInfo[]>("get_environments_info");
		return infos;
	} catch (error) {
		logger.error("Failed to get environments info", { cause: error });
		return [];
	}
});
$environmentsInfo.debugLabel = "environmentsInfo";

export const $environmentNames = atom(async (get) => {
	const infos = await get($environmentsInfo);
	return infos.map((info) => info.name);
});
$environmentNames.debugLabel = "environmentNames";

export const $environmentVars = atomFamily((name: string) =>
	atomWithRefresh(async () => {
		if (!env.isDesktopApp || !name) {
			return {};
		}

		try {
			const vars = await invoke<EnvironmentVars>("get_environment_vars", { name });
			return vars;
		} catch (error) {
			logger.error("Failed to get environment vars", { name, cause: error });
			return {};
		}
	})
);

const $draftSelectedEnvironmentName = atom(undefined as string | undefined);
$draftSelectedEnvironmentName.debugPrivate = true;

const $cachedSelectedEnvironmentName = atom(async () => {
	const cache = getCache();

	try {
		const settings = await cache.settings.get();
		return settings.selectedEnvironment ?? null;
	} catch (error) {
		logger.error("Failed to get selected environment from cache", { cause: error });
		return null;
	}
});
$cachedSelectedEnvironmentName.debugPrivate = true;

export const $selectedEnvironmentName = atom(
	async (get): Promise<string> => {
		const draft = get($draftSelectedEnvironmentName);
		const cached = await get($cachedSelectedEnvironmentName);

		// Prefer draft when set. Environment names can come from either local
		// .env files or remote workspace profiles.
		if (draft !== undefined) {
			return draft;
		}

		// Fall back to cached when set for the same reason.
		if (cached !== null) {
			return cached;
		}

		// "default" environment is always created on app boot
		return "default";
	},
	async (_get, set, name: string) => {
		const cache = getCache();

		set($draftSelectedEnvironmentName, name);

		try {
			await cache.settings.update({ selectedEnvironment: name });
		} catch (error) {
			logger.error("Failed to persist selected environment", { name, cause: error });
		}
	}
);
$selectedEnvironmentName.debugLabel = "selectedEnvironmentName";

async function runScript(script: string) {
	if (!env.isDesktopApp) {
		throw new Error("Cannot run script in non-desktop app");
	}
	const enc = new TextEncoder();
	const encodedScript = enc.encode(script);
	const dec = new TextDecoder();
	const result = await invoke<ScriptOutput>("run_script", {
		script: Array.from(encodedScript),
	});
	return {
		status: result.status,
		stdout:
			result.stdout.length > 0 ? dec.decode(new Uint8Array(result.stdout)).trim() : undefined,
		stderr:
			result.stderr.length > 0 ? dec.decode(new Uint8Array(result.stderr)).trim() : undefined,
	};
}

async function resolve1PasswordSecretRef(varName: string, ref: string) {
	try {
		const secretValue = await runScript(`op read ${ref}`);
		if (secretValue.status !== 0) {
			throw new Error(`Error resolving secret reference '${ref}' for var ${varName}`);
		}
		return secretValue.stdout;
	} catch (err) {
		logger.error("Failed to fetch secret from 1Password", { error: err });
		throw err;
	}
}

async function resolveShellOutput(varName: string, shellCmd: string) {
	// Sort of a hack but it works.
	// Assign the result of the command to be executed to variable.
	let cmdToExecute = `cmd=${shellCmd}
echo $cmd`;
	if (env.isWindows) {
		// On Windows, variables are declared with a leading `$`.
		cmdToExecute = `$cmd=${shellCmd}
echo $cmd`;
	}
	try {
		const shellCmdOutput = await runScript(cmdToExecute);
		if (shellCmdOutput.status !== 0) {
			throw new Error(
				`Error resolving value for var ${varName}. '${shellCmd}' returned a non-zero exit code.`
			);
		}
		return shellCmdOutput.stdout;
	} catch (err) {
		logger.error("Failed to fetch value from shell command", { error: err });
		throw err;
	}
}

// Re-export interpolateEnvVars so external consumers of this barrel still find it.
// The function lives in its own module to avoid pulling the heavy atom dependency
// chain into pure-utility tests.
export { interpolateEnvVars };

/**
 * Resolve op:// secret references and $() shell commands in a vars record.
 * Shared by $resolvedEnvironmentVars and $resolvedDocumentEnvironmentVars.
 */
async function resolveSpecialVars(get: Getter, vars: EnvironmentVars): Promise<EnvironmentVars> {
	const resolved = Object.assign({}, vars);

	for (const varName in resolved) {
		const value = resolved[varName];
		if (value.startsWith("op://")) {
			const cmdAvailability = await get($commandAvailability);
			if (!cmdAvailability.onePassword.installed) {
				throw new Error(
					`Cannot resolve 1Password secret reference ${value}. 1Password CLI is not installed or was not found in your PATH.`
				);
			}

			const secretValue = await resolve1PasswordSecretRef(varName, value);
			resolved[varName] = secretValue || "";
		} else if (value.startsWith("$(")) {
			const cmdValue = await resolveShellOutput(varName, value);
			resolved[varName] = cmdValue || "";
		}
	}
	return resolved;
}

/**
 * Resolved environment vars for a given environment name.
 * Reads raw vars via Tauri and resolves op:// and $() references.
 * This is the reusable core that $selectedEnvironmentVars delegates to.
 */
export const $resolvedEnvironmentVars = atomFamily((name: string) =>
	atom(async (get) => {
		if (!name) {
			return {};
		}

		const vars = await get($environmentVars(name));
		return resolveSpecialVars(get, vars);
	})
);

export const $selectedEnvironmentVars = atom(async (get) => {
	const name = await get($selectedEnvironmentName);
	return get($resolvedEnvironmentVars(name));
});
$selectedEnvironmentVars.debugLabel = "selectedEnvironmentVars";

/**
 * Fully resolved document environment vars: interpolates and resolves op:// and $()
 * in document-scoped vars, then merges on top of already-resolved global vars.
 * Only the document portion is processed — global vars are already resolved
 * by $resolvedEnvironmentVars and are not re-processed.
 */
export const $resolvedDocumentEnvironmentVars = atomFamily(
	(params: { documentId: string; environmentName: string }) =>
		atom(async (get) => {
			const globalVars = await get($resolvedEnvironmentVars(params.environmentName));

			const rawDocVars = await get(
				$documentEnvironmentRawVars({
					documentId: params.documentId,
					environmentName: params.environmentName,
				})
			);

			// Nothing to merge — fast path
			if (Object.keys(rawDocVars).length === 0) {
				return globalVars;
			}

			// Interpolate ${VAR_NAME} references against global vars,
			// then resolve op:// and $() — only on document vars.
			const interpolatedDocVars: EnvironmentVars = {};
			for (const [key, value] of Object.entries(rawDocVars)) {
				interpolatedDocVars[key] = interpolateEnvVars(value, globalVars);
			}

			const resolvedDocVars = await resolveSpecialVars(get, interpolatedDocVars);

			return { ...globalVars, ...resolvedDocVars };
		}),
	isEqual
);

// Raw text atoms for reading/writing .env files directly
export const $environmentText = atomFamily((name: string) =>
	atom(async (get) => {
		if (!env.isDesktopApp || !name) {
			return "";
		}

		const pathsApi = paths();
		const fsApi = fs();
		const envDirPath = await get(pathsApi.getEnvironmentsDirPath());
		const filePath = await get(fsApi.pathJoin([envDirPath, `${name}.env`]));

		try {
			const text = await get(fsApi.readTextFile({ filePath }));
			return text;
		} catch (error) {
			// File might not exist yet (new environment)
			logger.debug("Failed to read environment text, file may not exist", { name, error });
			return "";
		}
	})
);

export const $writeEnvironmentText = atom(
	null,
	async (_get, set, params: { name: string; text: string }) => {
		if (!env.isDesktopApp) {
			return;
		}

		try {
			// Write via Rust — handles file I/O and updates the in-memory env cache
			await invoke("set_environment_vars", { name: params.name, text: params.text });
			// Refresh the environment vars atom to pick up new values
			set($environmentVars(params.name));
		} catch (error) {
			logger.error("Failed to write environment text", { name: params.name, cause: error });
			throw error;
		}
	}
);
$writeEnvironmentText.debugLabel = "writeEnvironmentText";

export const $deleteEnvironment = atom(null, async (get, set, name: string) => {
	if (!env.isDesktopApp) {
		return;
	}

	try {
		await invoke("delete_environment", { name });

		const selectedName = await get($selectedEnvironmentName);
		if (selectedName === name) {
			set($draftSelectedEnvironmentName, undefined);
		}

		set($environmentsInfo);
	} catch (error) {
		logger.error("Failed to delete environment", { name, cause: error });
		throw error;
	}
});
$deleteEnvironment.debugLabel = "deleteEnvironment";

// ─── Document-scoped environment atoms ──────────────────────────────────────

/**
 * Read raw .env text for a document-scoped environment file.
 * Path: environments/documents/{documentId}/{environmentName}.env
 */
export const $documentEnvironmentText = atomFamily(
	({ documentId, environmentName }: { documentId: string; environmentName: string }) =>
		atom(async (get) => {
			if (!env.isDesktopApp || !documentId || !environmentName) {
				return "";
			}

			const pathsApi = paths();
			const fsApi = fs();
			const docEnvDirPath = await get(pathsApi.getDocumentEnvironmentsDirPath(documentId));
			const filePath = await get(fsApi.pathJoin([docEnvDirPath, `${environmentName}.env`]));

			try {
				const text = await get(fsApi.readTextFile({ filePath }));
				return text;
			} catch (error) {
				// File might not exist yet
				logger.debug("Document env file not found, may not exist yet", {
					documentId,
					environmentName,
					error,
				});
				return "";
			}
		}),
	isEqual
);

/**
 * Read document-scoped environment vars via Tauri invoke.
 * These are the raw key-value pairs from the document's .env file,
 * before interpolation with global vars.
 */
export const $documentEnvironmentRawVars = atomFamily(
	({ documentId, environmentName }: { documentId: string; environmentName: string }) =>
		atomWithRefresh(async () => {
			if (!env.isDesktopApp || !documentId || !environmentName) {
				return {};
			}

			try {
				const vars = await invoke<EnvironmentVars>("get_environment_vars", {
					name: environmentName,
					documentId,
				});
				return vars;
			} catch (error) {
				logger.error("Failed to get document environment vars", {
					documentId,
					environmentName,
					cause: error,
				});
				return {};
			}
		}),
	isEqual
);

/**
 * Write raw .env text for a document-scoped environment file.
 * Creates the directory structure if it doesn't exist.
 */
export const $writeDocumentEnvironmentText = atom(
	null,
	async (_get, set, params: { documentId: string; environmentName: string; text: string }) => {
		if (!env.isDesktopApp) {
			return;
		}

		try {
			// Write via Rust — handles file I/O and directory creation
			await invoke("set_environment_vars", {
				name: params.environmentName,
				text: params.text,
				documentId: params.documentId,
			});
			// Refresh the raw vars atom to pick up new values
			set(
				$documentEnvironmentRawVars({
					documentId: params.documentId,
					environmentName: params.environmentName,
				})
			);
		} catch (error) {
			logger.error("Failed to write document environment text", {
				documentId: params.documentId,
				environmentName: params.environmentName,
				cause: error,
			});
			throw error;
		}
	}
);
$writeDocumentEnvironmentText.debugLabel = "writeDocumentEnvironmentText";

/**
 * Delete all document-scoped environment files for a document.
 * Removes the entire environments/documents/{documentId}/ directory.
 */
export const $deleteDocumentEnvironments = atom(null, async (get, _set, documentId: string) => {
	if (!env.isDesktopApp) {
		return;
	}

	const pathsApi = paths();
	const fsApi = fs();
	const docEnvDirPath = await get(pathsApi.getDocumentEnvironmentsDirPath(documentId));

	try {
		const dirExists = await get(fsApi.exists({ filePath: docEnvDirPath }));
		if (dirExists) {
			await remove(docEnvDirPath);
		}
	} catch (error) {
		logger.error("Failed to delete document environments", {
			documentId,
			cause: error,
		});
		throw error;
	}
});
$deleteDocumentEnvironments.debugLabel = "deleteDocumentEnvironments";
